import { Check, ChevronDown, LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TRUNCATE_THRESHOLD } from "@/lib/constants";
import { cn } from "@/lib/utils";

export interface AsyncOption {
  value: number | string;
  label: string;
  fullLabel?: string;
}

interface AsyncComboboxProps {
  value?: AsyncOption | null;
  onChange: (opt: AsyncOption | null) => void;
  loadOptions: (inputValue: string) => Promise<AsyncOption[]>;
  defaultOptions?: boolean | AsyncOption[];
  cacheOptions?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  buttonClassName?: string;
  disabled?: boolean;
}

const MAX_SELECTED_LENGTH = 50;

export default function AsyncCombobox({
  value = null,
  onChange,
  loadOptions,
  defaultOptions = true,
  cacheOptions = true,
  placeholder = "Escribe para ver más opciones…",
  searchPlaceholder = "Escribe para ver más resultados…",
  buttonClassName,
  disabled = false,
}: AsyncComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<AsyncOption[]>([]);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef<Map<string, AsyncOption[]>>(new Map());
  const debounceRef = useRef<number | undefined>(undefined);

  const providedDefaultOptions = useMemo(
    () => (Array.isArray(defaultOptions) ? defaultOptions : []),
    [defaultOptions],
  );

  const displayedOptions =
    query === "" && providedDefaultOptions.length > 0
      ? providedDefaultOptions
      : options;

  const selectedTitle = value?.fullLabel ?? value?.label ?? placeholder;
  const selectedLabel = useMemo(() => {
    if (!value) {
      return placeholder;
    }

    const { label } = value;
    return label.length > MAX_SELECTED_LENGTH
      ? `${label.slice(0, MAX_SELECTED_LENGTH)}…`
      : label;
  }, [value?.label, placeholder]);
  const buttonClasses = useMemo(
    () => cn("w-full h-9 px-3 justify-between text-sm", buttonClassName),
    [buttonClassName],
  );

  useEffect(() => {
    if (!cacheOptions) return;
    if (providedDefaultOptions.length === 0) return;
    cacheRef.current.set("", providedDefaultOptions);
  }, [cacheOptions, providedDefaultOptions]);

  const fetchOptions = async (q: string) => {
    if (q === "" && providedDefaultOptions.length > 0) {
      return;
    }
    if (cacheOptions && cacheRef.current.has(q)) {
      setOptions(cacheRef.current.get(q) ?? []);
      return;
    }
    setLoading(true);
    try {
      const opts = await loadOptions(q);
      if (cacheOptions) cacheRef.current.set(q, opts);
      setOptions(opts);
    } finally {
      setLoading(false);
    }
  };

  // Initial load on open when defaultOptions is true
  useEffect(() => {
    if (open && defaultOptions === true) {
      void fetchOptions("");
    }
  }, [open]);

  // Debounced fetch on query change
  useEffect(() => {
    if (!open) return;
    window.clearTimeout(debounceRef.current);

    if (query === "" && providedDefaultOptions.length > 0) {
      return;
    }

    debounceRef.current = window.setTimeout(() => {
      void fetchOptions(query);
    }, 250);
    return () => window.clearTimeout(debounceRef.current);
  }, [query, open, providedDefaultOptions.length]);

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={buttonClasses}
              disabled={disabled}
            >
              <span
                className={cn(
                  "truncate text-left",
                  !value && "text-muted-foreground",
                )}
              >
                {selectedLabel}
              </span>
              {loading ? (
                <LoaderCircle className="ml-auto h-4 w-4 shrink-0 animate-spin opacity-60" />
              ) : (
                <ChevronDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" align="center">
          {selectedTitle}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        className="p-0"
        align="start"
        style={{
          width: "var(--radix-popover-trigger-width)",
          maxWidth: "var(--radix-popover-content-available-width)",
        }}
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={(val) => setQuery(val)}
            className="h-9"
          />
          <CommandList className="max-h-72 overflow-auto">
            <CommandEmpty>
              {loading ? "Cargando…" : "Sin resultados"}
            </CommandEmpty>
            <CommandGroup>
              {displayedOptions.map((opt) => {
                const optionTitle = opt.fullLabel ?? opt.label;
                const isTruncated = optionTitle.length > TRUNCATE_THRESHOLD;

                const itemContent = (
                  <div className="flex min-w-0 items-center gap-2 w-full px-2 py-1.5">
                    <span className="truncate text-left">{opt.label}</span>
                    <Check
                      className={`ml-auto shrink-0 ${value && value.value === opt.value ? "opacity-100" : "opacity-0"}`}
                    />
                  </div>
                );

                return (
                  <CommandItem
                    key={String(opt.value)}
                    value={opt.label}
                    className="flex min-w-0 items-center gap-2 p-0"
                    onSelect={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                  >
                    {isTruncated ? (
                      <Tooltip>
                        <TooltipTrigger asChild>{itemContent}</TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          {optionTitle}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      itemContent
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
